<template>
  <div v-if="company.id && page_loaded" class="settings-company__wrapper">
    <h1 class="settings-title">{{ $tr('settings.title') }}</h1>

    <div v-if="!error" class="company">
      <div class="settings_left">
        <div class="settings-company__container">
          <div class="settings-company__upload">
            <div class="settings-company__item-content">
              <div class="settings-company__dragDrop" :class="{ 'has-files': hasFiles }">
                <input
                  id="file"
                  type="file"
                  name="file"
                  accept="image/*"
                  class="settings-company__dragDrop-input"
                  @change="filesChange($event.target.files)"
                />

                <div v-if="isDefaultFile && !deleteFile" class="upload__image">
                  <img v-if="companyLogo" :src="companyLogo" />
                </div>

                <div v-if="imgLink && !isDefaultFile" class="upload__image">
                  <img v-if="imgLink" :src="imgLink" />
                </div>

                <div class="upload__description">
                  <icon-upload class="settings-company__dragDrop-image" :fill="hasFiles ? '#fff' : '#00d26e'" />

                  <div class="settings-company__dragDrop-subtitle">
                    <span v-if="!hasFiles">
                      {{ $tr('settings.company.labels.uploadLogo') }}
                    </span>

                    <span v-else>{{ $tr('settings.company.labels.uploadNewLogo') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <vbutton
              v-if="companyLogo || fileList"
              color="transparent"
              icon="icon-bin"
              type="icon"
              @click="removeLogo"
            />
          </div>
        </div>
      </div>

      <div class="settings_right">
        <div class="settings_block">
          <div class="settings-subtitle">{{ $tr('profile.profileForm.info.title') }}</div>

          <div class="settings-company__container">
            <div class="settings-company__label">{{ $tr('settings.company.labels.companyName') }} *</div>
            <input v-model="companyName" class="settings-company__field" type="text" />
          </div>

          <div class="settings-company__container settings-company__container_city">
            <div class="settings-company__label settings-company__label_at-top">
              {{ $tr('settings.company.labels.city') }} *
            </div>

            <div class="settings-company__burgerSelect">
              <city-select
                :selected-geo="selectedGeo"
                :get-address-data="getAddressData"
                :delete-geo="deleteGeo"
                class="settings-company__field"
              />
            </div>
          </div>

          <div class="settings-company__container settings-company__container_language">
            <div class="settings-company__label settings-company__label_at-top">
              {{ $tr('settings.company.labels.language') }}
            </div>
            <div class="settings-company__burgerSelect">
              <languages-select v-model="companyLanguage" :has-label="false" class="settings-company__field" />
            </div>
          </div>
        </div>

        <div class="settings_block">
          <div class="settings-subtitle">
            {{ $tr('settings.company.labels.time_format') }}
          </div>

          <div class="settings-company__container">
            <div class="time_format_block">
              <div class="time_format_row">
                <vcircle :value="time_format === 24" @input="set_time_format(24)" />

                <label class="time_format_label" @click="set_time_format(24)">
                  <span class="black">{{ $tr('settings.company.labels.time_label.24') }}</span>
                  <span class="grey">{{ time_example_24 }}</span>
                </label>
              </div>

              <div class="time_format_row">
                <vcircle :value="time_format === 12" @input="set_time_format(12)" />

                <label class="time_format_label" @click="set_time_format(12)">
                  <span class="black">{{ $tr('settings.company.labels.time_label.12') }}</span>
                  <span class="grey">{{ time_format_12 }}</span>
                </label>
              </div>
            </div>
          </div>

          <div class="settings-company__container settings-company__container_submit">
            <div class="actions-panel">
              <div class="buttons-group">
                <vbutton label="settings.company.buttons.save" :disabled="!canBeSaved" @click="save" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <div class="error">
        <p>{{ error.message }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import axios from 'axios';
import CitySelect from '@/components/shared/CitySelect';
import LanguagesSelect from '@/components/vacancy/preferences/SettingsLanguagesSelect';
import { get_address } from '@/_helpers/utils';

export default {
  name: 'VcvSettingsCompany',

  components: {
    CitySelect,
    LanguagesSelect
  },

  data() {
    return {
      companyInfo: null,
      country: {},
      language: {},
      fileList: null,
      imgLink: null,
      time_format: 24,
      page_loaded: false,
      deleteFile: false
    };
  },

  computed: {
    time_example_24() {
      return this.$moment(new Date()).format('HH:mm');
    },

    time_format_12() {
      return this.$moment(new Date(), ['hh:mm a'], 'en').format('hh:mm a');
    },

    canBeSaved() {
      if (!this.companyName || !this.companyName.trim()) {
        return false;
      }

      if (!this.selectedGeo || !this.selectedGeo.place_id) {
        return false;
      }

      return true;
    },

    state() {
      return this.$store.state.company;
    },

    error() {
      return this.$store.state.company.error;
    },

    company() {
      return this.$store.getters['company/one'];
    },

    companyLogo() {
      const company = this.company;

      return company ? company.logo : null;
    },

    companyLanguage: {
      get: function () {
        if (this.language.code) {
          return this.language;
        }

        return this.company ? this.company.language : null;
      },

      set: function (val) {
        this.language = val;
      }
    },

    userEmail() {
      return this.$store.getters['user/current'] ? this.$store.getters['user/current'].email : '';
    },

    selectedGeo: {
      get: function () {
        const geo = this.company ? this.company.geo : null;

        return geo || {};
      },

      set: function (val) {
        this.$store.commit('company/update', {
          model: this.company,
          data: {
            geo: val
          }
        });
      }
    },

    companyName: {
      get: function () {
        return this.company.name;
      },

      set: function (val) {
        this.$store.commit('company/update', {
          model: this.company,
          data: {
            name: val
          }
        });
      }
    },

    isDefaultFile() {
      if (this.companyLogo && !this.fileList) {
        return true;
      }

      return false;
    },

    hasFiles() {
      if (this.deleteFile) {
        return false;
      }
      return this.fileList || this.isDefaultFile;
    }
  },

  watch: {
    company(val) {
      this.selectedGeo = val.geo;
      this.time_format = val.time_format;
    }
  },

  created() {
    this.fetch()
      .then(() => {
        this.page_loaded = true;
        this.time_format = this.company.time_format;
      })
      .catch(() => {
        this.page_loaded = true;
      });
  },

  beforeMount() {
    this.selectedGeo = this.company.geo;
  },

  methods: {
    ...mapActions('company', ['fetch']),

    set_time_format(v) {
      this.time_format = v;
    },

    removeLogo() {
      if (this.fileList) {
        this.fileList = null;
        this.imgLink = null;
      } else {
        this.deleteFile = true;
      }
    },

    // countryChange() {
    //   this.deleteGeo();
    // }

    getAddressData(addressData, placeResultData) {
      this.selectedGeo = get_address(addressData, placeResultData);
    },

    deleteGeo() {
      this.selectedGeo = {
        place_id: null,
        description: null
      };
    },

    save() {
      const model = Object.assign({}, this.company, {
        city: this.selectedGeo.description,
        language: this.language,
        time_format: this.time_format
      });

      let logoFile;
      if (this.fileList) {
        logoFile = this.fileList[0];
      }

      this.$store
        .dispatch('company/save', {
          model,
          logoBlob: logoFile
        })
        .then(() => {
          if (this.deleteFile) {
            return axios.delete(`/1.0/user-companies/${this.company.id}/logo`);
          }
        })
        .then(() => this.$store.dispatch('user/userInfoRequest', this.userEmail))
        .catch(err => {
          if (err.response === undefined) {
            this.$noty.error(this.$tr('common.error.networkError'));
          } else {
            this.$store.dispatch('global/setError', err);
          }
        });
    },

    filesChange(fileList) {
      this.fileList = fileList;

      const file = fileList[0];
      this.deleteFile = false;

      if (typeof FileReader === 'function') {
        const reader = new FileReader();

        reader.onload = event => {
          this.imgLink = event.target.result;
        };

        reader.readAsDataURL(file);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.settings-title {
  margin-bottom: 20px;
  @include mixin-text-header-bigger;
}

.settings-subtitle {
  @include mixin-text-header-big;
  margin-bottom: 1rem;
  padding: 0;
}

.settings_left {
  width: 288px;
  margin-right: 1.5rem;
}

.settings_right {
  flex: 1;
}

.settings-company__wrapper {
  padding-bottom: 100px;
  max-width: 800px;
}

.settings {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
}

.settings-actions {
  display: flex;
  justify-content: space-between;
  width: 330px;
}

.company {
  display: flex;
  align-items: flex-start;
}

.settings-company__container {
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  margin-bottom: 4px;
}

.settings-company__label {
  display: block;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 16px;
  color: #233346;
}

.settings-company__label_at-top {
  align-items: flex-start;
}

.settings-company__burgerSelect,
.settings-company__field {
  width: 100%;

  .multiselect {
    width: 100%;
    max-width: 100% !important;
  }
}

input[type='text'] {
  width: 100%;
}

.settings-company__multiselect .multiselect__tags {
  text-align: left;
  border-radius: 4px;
  background-color: #ffffff;
  border: solid 1px $green;
  padding: 5px 10px;
  font-size: 16px;
}

.settings-company__item-content {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
}

.settings-company__dragDrop {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  font-size: 15px;
  line-height: 20px;
  color: #afafaf;
  cursor: pointer;
  transition: background-color 0.7s;
  border: 1px dashed $green;
  border-radius: 2px;
  color: $green;
  box-sizing: border-box;
  height: 288px;
  width: 288px;

  &.has-files {
    max-height: 288px;
    height: auto;
  }

  &:hover {
    border-style: solid;
  }

  & input.settings-company__dragDrop-input {
    opacity: 0;
    top: 0;
    left: 0;
    width: 100%;
    position: absolute;
    z-index: 10;
    cursor: pointer;

    &#file {
      height: 100%;
    }
  }

  img {
    max-height: 264px;
    height: auto;
    max-width: 264px;
    width: auto;
  }
}

.settings-company__image {
  max-width: 288px;
  margin-right: 25px;
}

.settings-company__dragDrop-image {
  font-size: 15px;
  margin-right: 10px;
}

.settings-company__upload-input {
  opacity: 0;
  visibility: hidden;
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  z-index: -1;
}

.upload__description {
  display: flex;
  justify-content: center;
  align-items: center;
}

.upload__image {
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.has-files {
  border: 1px solid #e5e5e5;

  .upload__description {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    background-color: #fff;
    opacity: 0;
  }

  &:hover {
    color: #fff;

    .upload__description {
      opacity: 1;
      z-index: 1;
      background-color: rgba(127, 127, 127, 0.8);
    }
  }
}

.settings-company__upload-label {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 20px;
  cursor: pointer;
}

.settings-company__info {
  width: 288px;
  font-size: 14px;
  text-align: left;
  color: #afafaf;
}

.settings-company__info-item {
  margin-bottom: 10px;
}

.settings-company__info-item:last-child {
  margin-bottom: 0;
}

.settings-company__upload {
  width: 100%;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
}

.settings-company__dragDrop-subtitle {
  font-size: 14px;
  text-align: center;
  text-transform: uppercase;
}

.time_format_block {
  .time_format_row {
    display: flex;
    margin-bottom: 5px;
    align-items: center;
    min-height: 22px;
    max-height: 22px;

    .time_format_label {
      display: flex;
      align-items: center;
      // font-family: Roboto;
      font-style: normal;
      font-weight: normal;
      line-height: 24px;
      font-size: 15px;
      letter-spacing: 0.005em;
      color: #233346;
      margin-left: 8px;
      cursor: pointer;

      & span {
        margin-right: 8px;
        cursor: pointer;

        &.grey {
          color: #9faab6;
        }
      }
    }
  }
}

.settings_block {
  margin-bottom: 32px;
}

.settings_block-title {
  font-style: normal;
  font-weight: bold;
  font-size: 17px;
  line-height: 24px;
  color: #233346;
  margin-bottom: 12px;
}

.settings-company__wrapper {
  .grey,
  .black {
    background-color: transparent !important;
    border-color: transparent !important;
  }
}

.actions-panel {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 52px;
}
</style>
